/**
 * Gallery Item Component for Interactive Image Display
 * 
 * Individual gallery item component that handles image display, modal interaction,
 * and gallery navigation. Works in conjunction with the Gallery component to
 * provide a complete image viewing experience.
 * 
 * Core Features:
 * - Click-to-expand image viewing with full-screen modal
 * - Gallery navigation (next/previous) within modal
 * - Responsive image sizing with Next.js optimization
 * - Mobile-aware interaction patterns
 * - Image preloading for smooth navigation
 * - Fallback handling for missing or invalid images
 * 
 * Modal Integration:
 * - Full-screen image viewing with ImageModal
 * - Touch/swipe navigation on mobile devices
 * - Keyboard navigation (arrow keys, escape)
 * - Smooth transitions between images
 * - Background blur and scroll prevention
 * 
 * Performance Features:
 * - Intelligent image preloading for adjacent items
 * - Mobile detection for optimized interactions
 * - Lazy loading via Next.js Image component
 * - Event-driven preload coordination
 * - Efficient re-rendering with stable references
 * 
 * Accessibility:
 * - Descriptive alt text from CMS or fallbacks
 * - Keyboard navigation support
 * - Screen reader compatible image descriptions
 * - Focus management for modal interactions
 * 
 * @param {object} item - Gallery item data from Sanity CMS
 * @param {object} item.image - Sanity image object with asset reference
 * @param {string} item.caption - Optional caption text for the image
 * @param {Array} allItems - Full array of gallery items for navigation
 * @param {number} index - Current item index within the gallery
 * 
 * @description Interactive gallery item with modal viewing and navigation
 */

import React, { useState, useEffect, useCallback } from 'react';
import Image from 'next/image';
import { urlForImage } from '../../src/app/studio/lib/image';
import { sanityLoader } from '../../utils/sanityLoader';
import ImageModal from '../ui/ImageModal';

const GalleryItem = ({ item, allItems, index }) => {
  const [selectedImage, setSelectedImage] = useState(null);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isMobile, setIsMobile] = useState(false);
  
  // Check if we're on mobile on mount and window resize
  useEffect(() => {
    const checkIfMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };
    
    checkIfMobile();
    window.addEventListener('resize', checkIfMobile);
    
    return () => window.removeEventListener('resize', checkIfMobile);
  }, []);

  if (!item || !item.image) return null;

  // Get alt text from image or caption
  const altText = item.image.alt || item.caption || 'Gallery image';
  
  // Get image URL
  const imageUrl = urlForImage(item.image);
  
  // If no valid image URL, don't render the component
  if (!imageUrl) {
    console.warn('Invalid gallery image:', item.image);
    return null;
  }

  // Helper to get image URL by index
  const getImageUrlByIndex = (idx) => {
    if (!allItems || !allItems[idx] || !allItems[idx].image) return null;
    return urlForImage(allItems[idx].image);
  };

  // Request preloading of adjacent images
  const requestPreload = useCallback((currentIdx) => {
    if (!allItems || allItems.length <= 1) return;
    
    const nextIdx = (currentIdx + 1) % allItems.length;
    const prevIdx = (currentIdx - 1 + allItems.length) % allItems.length;
    
    const nextImageUrl = getImageUrlByIndex(nextIdx);
    const prevImageUrl = getImageUrlByIndex(prevIdx);
    
    // Dispatch event to request preloading
    if (nextImageUrl || prevImageUrl) {
      window.dispatchEvent(new CustomEvent('requestImagePreload', {
        detail: {
          nextImage: nextImageUrl,
          prevImage: prevImageUrl
        }
      }));
    }
  }, [allItems]);

  // Handler for navigating to the previous image
  const handlePrevImage = () => {
    if (allItems && allItems.length > 1) {
      const newIndex = (currentIndex - 1 + allItems.length) % allItems.length;
      setCurrentIndex(newIndex);
      
      const prevItem = allItems[newIndex];
      if (prevItem && prevItem.image) {
        const prevImageUrl = urlForImage(prevItem.image);
        const prevAltText = prevItem.image.alt || prevItem.caption || 'Gallery image';
        
        setSelectedImage({
          src: prevImageUrl,
          alt: prevAltText,
          galleryType: 'gallery'
        });
        
        // Preload next images in the sequence
        requestPreload(newIndex);
      }
    }
  };

  // Handler for navigating to the next image
  const handleNextImage = () => {
    if (allItems && allItems.length > 1) {
      const newIndex = (currentIndex + 1) % allItems.length;
      setCurrentIndex(newIndex);
      
      const nextItem = allItems[newIndex];
      if (nextItem && nextItem.image) {
        const nextImageUrl = urlForImage(nextItem.image);
        const nextAltText = nextItem.image.alt || nextItem.caption || 'Gallery image';
        
        setSelectedImage({
          src: nextImageUrl,
          alt: nextAltText,
          galleryType: 'gallery'
        });
        
        // Preload next images in the sequence
        requestPreload(newIndex);
      }
    }
  };

  // Handler for clicking on an image
  const handleImageClick = () => {
    if (!isMobile) {
      setCurrentIndex(index);
      setSelectedImage({
        src: imageUrl,
        alt: altText,
        galleryType: 'gallery'
      });
      
      // Preload adjacent images when opening modal
      requestPreload(index);
    }
  };

  return (
    <>
      <div 
        className="h-full w-full overflow-hidden rounded-md shadow-md hover:shadow-lg transition-all duration-300 cursor-pointer"
        onClick={handleImageClick}
      >
        <div className="relative h-full w-full">
          <Image
            src={imageUrl}
            alt={altText}
            fill
            loader={sanityLoader}
            sizes="(max-width: 640px) 100vw, (max-width: 1024px) 50vw, 33vw"
            className="object-cover transition-transform duration-500 hover:scale-105"
            loading="lazy"
          />
          {item.caption && (
            <div className="absolute inset-0 opacity-0 hover:opacity-100  flex items-end justify-start p-3">
              <div className="bg-white/10 backdrop-blur-sm rounded-lg p-3 border border-white/20 max-w-full">
                <div className="flex items-start">
                  <div className="text-accent-color mr-2 mt-0.5 flex-shrink-0">
                    <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M14.017 21v-7.391c0-5.704 3.731-9.57 8.983-10.609l.995 2.151c-2.432.917-3.995 3.638-3.995 5.849h4v10h-9.983zm-14.017 0v-7.391c0-5.704 3.748-9.57 9-10.609l.996 2.151c-2.433.917-3.996 3.638-3.996 5.849h3.983v10h-9.983z"/>
                    </svg>
                  </div>
                  <p className="text-white text-xs md:text-sm font-neuropol leading-relaxed break-words">
                    {item.caption}
                  </p>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
      
      {/* Image Modal - only shown when an image is selected and not on mobile */}
      {selectedImage && !isMobile && (
        <ImageModal 
          image={selectedImage.src} 
          alt={selectedImage.alt} 
          onClose={() => setSelectedImage(null)}
          onPrev={handlePrevImage}
          onNext={handleNextImage}
          showNavigation={allItems && allItems.length > 1}
        />
      )}
    </>
  );
};

export default GalleryItem; 